import React from 'react';
import { Link, useNavigate } from 'react-router-dom';
import Layout from '../components/Layout/Layout';
import { Container, Title, Grid, Button, Paper, Text, Group } from '@mantine/core';
import { useAuth } from '../contexts/AuthContext';
// Beta gate removed - open registration
import CompetitionSection from '../components/Profile/CompetitionSection';
import PastCompetitionsSection from '../components/Profile/PastCompetitionsSection';
import SchoolPastSeasonsSection from '../components/Profile/SchoolPastSeasonsSection';
import SchoolAccountSection from '../components/Profile/SchoolAccountSection';
import AccountInfoPanel from '../components/Profile/AccountInfoPanel';
import QuickLinksPanel from '../components/Profile/QuickLinksPanel';

const Profile = () => {
  const { user, signOut } = useAuth();
  const navigate = useNavigate();

  const handleSignOut = async () => {
    try {
      await signOut();
      navigate('/');
    } catch (error) {
      console.error('Error signing out:', error);
    }
  };

  return (
    <Layout
      title="My Profile | Prix du Canada Ballet Competition"
      description="Manage your Prix du Canada account, view your registrations, and access exclusive dancer resources."
      keywords="ballet competition profile, dancer dashboard, prix du canada account, ballet competition account"
    >
      {/* Profile Content */}
      <section className="section" style={{background: 'var(--light-gray)', paddingTop: '4rem', paddingBottom: '4rem'}}>
        <Container size="xl">
          {user ? (
            <>
              {/* Welcome Header */}
              <div style={{ marginBottom: '2.5rem' }}>
                <Group justify="space-between" align="center">
                  <Title
                    order={1}
                    size="h2"
                    style={{
                      fontFamily: "'Playfair Display', serif",
                      color: 'var(--charcoal)',
                      marginBottom: '0'
                    }}
                  >
                    Welcome back, {user.user_metadata?.full_name || user.email.split('@')[0]}!
                  </Title>
                  <Button
                    variant="subtle"
                    onClick={handleSignOut}
                    style={{ color: 'var(--soft-gray)' }}
                  >
                    Sign Out
                  </Button>
                </Group>
              </div>

              {/* Two-Column Layout - REVERSED */}
              <Grid gutter="xl">
                {/* Left Column (30%) - Account Info & Links */}
                <Grid.Col span={{ base: 12, md: 4 }}>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>
                    {/* Account Information Panel */}
                    <AccountInfoPanel user={user} />

                    {/* Quick Links Panel */}
                    <QuickLinksPanel />
                  </div>
                </Grid.Col>

                {/* Right Column (70%) - Registration Sections */}
                <Grid.Col span={{ base: 12, md: 8 }}>
                  {/* Competition Registration Section */}
                  <div style={{ marginBottom: '2rem' }}>
                    <CompetitionSection />
                  </div>

                  {/* Studio Portal (only renders if user owns a school_account) */}
                  <div style={{ marginBottom: '2rem' }}>
                    <SchoolAccountSection />
                  </div>

                  {/* Past Competitions (only renders once a season has ended) */}
                  <div style={{ marginBottom: '2rem' }}>
                    <PastCompetitionsSection />
                  </div>

                  {/* Past studio seasons (only renders for schools with history) */}
                  <div style={{ marginBottom: '2rem' }}>
                    <SchoolPastSeasonsSection />
                  </div>

                </Grid.Col>
              </Grid>
            </>
          ) : (
            <Container size="sm">
              <Paper
                radius="md"
                p="xl"
                withBorder
                style={{
                  borderTop: '4px solid var(--primary-gold)',
                  boxShadow: '0 10px 30px rgba(0, 0, 0, 0.1)',
                  textAlign: 'center'
                }}
              >
                <div style={{
                  width: '80px',
                  height: '80px',
                  background: 'linear-gradient(135deg, var(--primary-gold), var(--deep-gold))',
                  borderRadius: '50%',
                  margin: '0 auto 1.5rem',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}>
                  <span style={{ color: 'white', fontSize: '2rem' }}>🔒</span>
                </div>

                <Title
                  order={2}
                  style={{
                    fontFamily: "'Playfair Display', 'Georgia', serif",
                    marginBottom: '1rem',
                    color: 'var(--charcoal)'
                  }}
                >
                  Sign In Required
                </Title>

                <Text style={{ color: 'var(--soft-gray)', marginBottom: '2rem', fontSize: '1.1rem' }}>
                  Please sign in to access your profile and manage your account.
                </Text>

                <div style={{ display: 'grid', gap: '1rem' }}>
                  <Link to="/login?redirect=/profile" style={{ textDecoration: 'none' }}>
                    <Button
                      fullWidth
                      style={{
                        background: 'linear-gradient(135deg, var(--primary-gold), var(--deep-gold))',
                        fontWeight: 600
                      }}
                    >
                      Sign In
                    </Button>
                  </Link>

                  <Link to="/register" style={{ textDecoration: 'none' }}>
                    <Button
                      fullWidth
                      variant="outline"
                      style={{
                        borderColor: 'var(--primary-gold)',
                        color: 'var(--primary-gold)',
                        fontWeight: 600
                      }}
                    >
                      Create Account
                    </Button>
                  </Link>
                </div>
              </Paper>
            </Container>
          )}
        </Container>
      </section>
    </Layout>
  );
};

export default Profile;
